import { toast } from "sonner"

import type { TFunction } from "i18next"

export type SharedSessionAction = "left" | "hidden" | "left_and_hidden"

type SharedSessionParticipant = {
  oid: string
  type: "user" | "group"
}

export function getSharedSessionAccessState(
  participants: SharedSessionParticipant[],
  currentUserOid: string | undefined,
  currentUserGroupSet: Set<string>
) {
  const hasDirectShare = participants.some(
    (participant) =>
      participant.type === "user" && Boolean(currentUserOid) && participant.oid === currentUserOid
  )
  const hasGroupShare = participants.some(
    (participant) => participant.type === "group" && currentUserGroupSet.has(participant.oid)
  )

  return { hasDirectShare, hasGroupShare }
}

export function getSharedSessionActionLabel(t: TFunction, hasDirectShare: boolean) {
  return hasDirectShare
    ? t("chatsession.leave_shared_chat", { defaultValue: "Leave shared chat" })
    : t("chatsession.hide_shared_chat", { defaultValue: "Hide from me" })
}

export function getSharedSessionConfirmTitle(t: TFunction, hasDirectShare: boolean) {
  return hasDirectShare
    ? t("chatsession.confirm_leave_shared_chat_title", { defaultValue: "Leave shared chat?" })
    : t("chatsession.confirm_hide_shared_chat_title", { defaultValue: "Hide shared chat?" })
}

export function getSharedSessionConfirmDescription(
  t: TFunction,
  hasDirectShare: boolean,
  hasGroupShare: boolean
) {
  if (hasDirectShare && hasGroupShare) {
    return t("chatsession.confirm_leave_and_hide_shared_chat_description", {
      defaultValue:
        "You will be removed from direct sharing and this chat will be hidden from your shared list.",
    })
  }

  if (hasDirectShare) {
    return t("chatsession.confirm_leave_shared_chat_description", {
      defaultValue: "You will lose access to this shared chat.",
    })
  }

  return t("chatsession.confirm_hide_shared_chat_description", {
    defaultValue: "This chat will be hidden from your shared list, but your group access remains.",
  })
}

export function showSharedSessionActionSuccessToast(t: TFunction, action: SharedSessionAction) {
  if (action === "left") {
    toast.success(
      t("chatsession.leave_shared_chat_success", { defaultValue: "You left the shared chat" })
    )
    return
  }

  if (action === "hidden") {
    toast.success(
      t("chatsession.hide_shared_chat_success", {
        defaultValue: "Chat hidden from your shared list",
      })
    )
    return
  }

  toast.success(
    t("chatsession.leave_and_hide_shared_chat_success", {
      defaultValue: "Direct access removed and chat hidden from your shared list",
    })
  )
}

export function showSharedSessionActionErrorToast(t: TFunction) {
  toast.error(
    t("chatsession.leave_shared_chat_error", {
      defaultValue: "Failed to update shared chat visibility",
    })
  )
}
